import React from 'react'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { MenuHeaderAction } from 'components/menu/items'
import {
  PanelAction,
  PanelAdd,
  PanelFooter,
  PanelRule,
  PanelRules,
  PanelSection,
  PanelSegmentChoice,
  PanelSegmentSelect,
  PanelSwitch
} from 'components/menu/panel'
import { ITableViewColumn } from 'types'
import { BLOCK_GROUP_BY, COLUMN_NAME } from 'components/spreadsheet/constants/const'
import { CollapseAll, ExpandAll, Navigation, SplitValues } from 'components/icons'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { directionOptions } from '../sort/directions'

interface GroupMenuProps extends MenuProps {
  handleToggleCollapseAll: () => void
}

const GroupMenu: React.FC<GroupMenuProps> = ({ id, menuState, setMenuState, width, handleToggleCollapseAll }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { viewDetails, userConfiguration, isAdmin } = spreadsheetData
  const groupSettings = userConfiguration.groupSettings
  const columns = viewDetails.columns
  const busy = spreadsheetData.refreshing

  const columnOption = (column: ITableViewColumn) => ({
    value: column.publicId,
    label: column.name,
    icon: <IconSelector kind={column.kind} />
  })
  // Visible columns a group can be made from, other than the ones already grouped by.
  const freeColumns = columns.filter(
    (column) =>
      !BLOCK_GROUP_BY.includes(column.kind) &&
      !userConfiguration.hiddenColumns.includes(column.publicId) &&
      !groupSettings.some((group) => group.columnId === column.publicId)
  )

  const addGroup = (columnId: string) => {
    const column = columns.find((c) => c.publicId === columnId)
    if (column) setSpreadsheetData({ type: 'ADD_GROUP', columnId, columnName: column.name })
  }

  // Swap a group's column in place, keeping its position and direction.
  const changeColumn = (index: number, columnId: string) => {
    const current = groupSettings[index]
    const column = columns.find((c) => c.publicId === columnId)
    if (!current || !column || current.columnId === columnId) return
    setSpreadsheetData({ type: 'CHANGE_GROUP_COLUMN', index, columnId, columnName: column.name })
  }

  const clearAll = () =>
    groupSettings.forEach((group) => setSpreadsheetData({ type: 'DELETE_GROUP', columnId: group.columnId }))

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={3000}
      title="Group"
      action={
        groupSettings.length > 0 && (
          <MenuHeaderAction onClick={clearAll} disabled={busy}>
            Clear all
          </MenuHeaderAction>
        )
      }
    >
      {groupSettings.length > 0 && (
        <PanelRules
          ids={groupSettings.map((group) => group.columnId)}
          onReorder={
            busy
              ? undefined
              : (existingIndex, newIndex) => setSpreadsheetData({ type: 'CHANGE_GROUP_ORDER', existingIndex, newIndex })
          }
        >
          {groupSettings.map((group, index) => {
            const column = columns.find((c) => c.publicId === group.columnId)
            return (
              <PanelRule
                key={group.columnId}
                id={group.columnId}
                removeLabel={`Remove group by ${group.columnName}`}
                onRemove={() => setSpreadsheetData({ type: 'DELETE_GROUP', columnId: group.columnId })}
                disabled={busy}
                data-testid="group-menu-group-item"
              >
                <PanelSegmentSelect
                  ariaLabel="Column"
                  tone="field"
                  options={[...(column ? [columnOption(column)] : []), ...freeColumns.map(columnOption)]}
                  selected={[group.columnId]}
                  onPick={(columnId) => changeColumn(index, columnId)}
                  menuWidth={240}
                  className="max-w-[200px]"
                  disabled={busy}
                />
                <PanelSegmentChoice
                  lead="sorted by"
                  ariaLabel={`Order of the ${group.columnName} groups`}
                  options={directionOptions(column?.kind)}
                  value={group.direction}
                  onChange={(newDirection) =>
                    setSpreadsheetData({ type: 'CHANGE_GROUP_DIRECTION', columnId: group.columnId, newDirection })
                  }
                  disabled={busy}
                />
              </PanelRule>
            )
          })}
        </PanelRules>
      )}

      <PanelAdd
        data-tour="add-group"
        label="Add group"
        options={freeColumns.map(columnOption)}
        onPick={addGroup}
        disabled={busy}
        noOptionsMessage={`Every visible ${COLUMN_NAME} is already grouped by.`}
      />

      {groupSettings.length > 0 && isAdmin && (
        <PanelSection label="View preferences">
          <PanelSwitch
            label="Collapse all groups on view open"
            icon={<CollapseAll />}
            checked={!!viewDetails.collapsedGroupView}
            onChange={(collapsedGroupView) =>
              setSpreadsheetData({ type: 'CHANGE_COLLAPSED_GROUP_VIEW', collapsedGroupView })
            }
          />
          <PanelSwitch
            label="Show groups as a tree"
            icon={<Navigation />}
            checked={!!viewDetails.treeGroupView}
            onChange={(treeGroupView) => setSpreadsheetData({ type: 'CHANGE_TREE_GROUP_VIEW', treeGroupView })}
          />
          <PanelSwitch
            label="Split multi-select values (reloads)"
            icon={<SplitValues />}
            checked={!!viewDetails.unpackMultiselectGroupView}
            onChange={(unpackMultiselectGroupView) =>
              setSpreadsheetData({ type: 'CHANGE_UNPACK_MULTISELECT_GROUP_VIEW', unpackMultiselectGroupView })
            }
          />
        </PanelSection>
      )}

      {groupSettings.length > 0 && (
        <PanelFooter>
          <PanelAction icon={<CollapseAll />} onClick={handleToggleCollapseAll}>
            Collapse groups
          </PanelAction>
          <PanelAction icon={<ExpandAll />} onClick={() => setSpreadsheetData({ type: 'EXPAND_ALL_GROUPS' })}>
            Expand groups
          </PanelAction>
        </PanelFooter>
      )}
    </FloatingMenu>
  )
}

export default GroupMenu
